import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import useFileDownload from '@/hooks/useFiledDownload';
import ScheduledTransactionOverview from './ScheduledTransactionOverview';
import usePostCancelScheduledFund from '@/hooks/useApi/usePostCancelScheduledFund';
import usePostCancelScheduledWithdraw from '@/hooks/useApi/usePostCancelScheduledWithdraw';
import useFetchScheduledTransactions from '@/hooks/useApi/useFetchScheduledTransactions';
import TransactionsHistory from './Transactions/TransactionHistory';
import styles from '../ScheduledTransactions.module.scss';

import CancelTransactionDialogue from '../../Dialogue/CancelTransaction/CancelTransactionDialogue';
import CancelTransactionContext, {
  CancelTransactionCtx,
} from '../../Dialogue/CancelTransaction/CancelTransactionContext';
import { CancelTransactionDialogueHandler, DialogueHandlerProps } from '../../Dialogue/types';
import { useTranslation } from 'react-i18next';
import { UtilButton } from 'UI';

const { Box, Title, Button, DropDown } = vp;

type FC = FunctionComponent;

type DataProps = {
  transactionId: string;
  activeModule: string;
  hasNewTransactionButton: boolean;
};
type URLParams = {
  TransactionID: string;
};

const ScheduledTransactionDetailsContainer: FC = () => {
  const { state } = useLocation<DataProps>();
  const { TransactionID } = useParams<URLParams>();
  const { onSubNavClick } = useNavMenu();
  const { printFile, downloadPdfPage } = useFileDownload();
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [hasNewStatus, setNewStatus] = useState(true);
  const { permissions } = usePermissions();
  const [shouldCancelFund, setShouldCancelFund] = useState(false);
  const [shouldCancelWithdraw, setShouldCancelWithdraw] = useState(false);
  const [showCancelTransactionConfirmAlert, setShowCancelTransactionConfirmAlert] = useState(false);
  const [showCancelTransactionErrorAlert, setShowCancelTransactionErrorAlert] = useState(false);
  const [sendCancelTransactionError, setSendCancelTransactionError] = useState('');
  const [cancelScheduledFundResponse, postCancelScheduledFund] = usePostCancelScheduledFund({});
  const [cancelScheduledWithdrawResponse, postCancelScheduledWithdraw] = usePostCancelScheduledWithdraw({});
  const [scheduledTransactionsResponse, fetchScheduledTransactions] = useFetchScheduledTransactions({});
  const { t } = useTranslation('translations', { keyPrefix: 'ScheduledTransactions.Details' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const history = useHistory();

  const CancelTransaction: CancelTransactionCtx = {
    showCancelTransactionConfirmAlert,
    showCancelTransactionErrorAlert,
    sendCancelTransactionError,
  };

  const handlers = {
    handleCancelTransactionDialogue: (handlerProps: DialogueHandlerProps<CancelTransactionDialogueHandler>) => {
      const { type, state: handlerState, action = 'default' } = handlerProps;

      const dialogueHandlers: CancelTransactionDialogueHandler = {
        alert: {
          success: {
            default: () => {
              setShowCancelTransactionConfirmAlert(false);
              if (Object.values(scheduledTransactionsResponse.ScheduledTransactions)[0]?.Type === 'fund') {
                setShouldCancelFund(true);
                postCancelScheduledFund({ ScheduledTransactionID: TransactionID });
              } else {
                setShouldCancelWithdraw(true);
                postCancelScheduledWithdraw({ ScheduledTransactionID: TransactionID });
              }
            },
          },
          fail: {
            default: () => {
              setShowCancelTransactionConfirmAlert(false);
            },
          },
        },
        error: {
          success: {
            default: () => {},
          },
          fail: {
            default: () => {
              setShowCancelTransactionErrorAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][handlerState];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  useEffect(() => {
    if (hasNewStatus) {
      fetchScheduledTransactions({
        ScheduledTransactionID: TransactionID,
      });
    }
  }, [TransactionID, hasNewStatus]);
  useEffect(() => {
    setNewStatus(false);
  }, [scheduledTransactionsResponse]);

  useEffect(() => {
    if (shouldCancelFund) {
      const { Success, ErrorMessage } = cancelScheduledFundResponse;

      if (Success === true) {
        setNewStatus(true);
      } else {
        setShowCancelTransactionErrorAlert(true);
        setSendCancelTransactionError(ErrorMessage);
      }
    }
    if (shouldCancelWithdraw) {
      const { Success, ErrorMessage } = cancelScheduledWithdrawResponse;

      if (Success === true) {
        setNewStatus(true);
      } else {
        setShowCancelTransactionErrorAlert(true);
        setSendCancelTransactionError(ErrorMessage);
      }
    }
    setShouldCancelFund(false);
    setShouldCancelWithdraw(false);
  }, [cancelScheduledFundResponse, cancelScheduledWithdrawResponse]);

  useEffect(() => {
    if (isExportingPDF) {
      downloadPdfPage('scheduledTransactions', 'scheduled-transactions');
    }
    if (isPrinting) {
      printFile('scheduledTransactions');
    }
    setIsExportingPDF(false);
    setIsPrinting(false);
  }, [isExportingPDF, isPrinting]);

  return (
    <CancelTransactionContext.Provider value={CancelTransaction}>
      <div id="scheduledTransactions">
        <Box flx="col" gapSize="16px">
          <UtilButton
            iconLeft="chevron_left"
            size="large"
            onClick={() => {
              if (history.length > 1) {
                history.goBack();
              } else {
                history.replace('/scheduled-transactions');
              }
            }}
          >
            {history.length > 1 ? tGlobal('Buttons.back') : t('title')}
          </UtilButton>
          <Box width="100%" flx="row" jc="sb">
            <Box mb="md" jc="fs" ai="center" gap="common">
              <Title title={t('title')} />
              <div className="hiddenFormsPrint">
                {state?.hasNewTransactionButton && (
                  <Button
                    id="start-new-transactions"
                    label={t('startNewTransactionButton')}
                    onClick={() => {
                      if (state.activeModule === 'fundMyAccount') {
                        onSubNavClick('fundMyAccount', '/fund-my-account', 'Fund My Account');
                      } else if (state.activeModule === 'bankAccountTransfer') {
                        onSubNavClick('bankAccountTransfer', '/bank-account-transfer', 'Bank Account Transfer');
                      } else if (state.activeModule === 'withdrawMyAccount') {
                        onSubNavClick('withdrawMyAccount', '/withdraw-my-account', 'Withdraw My Account');
                      } else if (state.activeModule === 'singleCollection') {
                        onSubNavClick('singleCollection', '/single-collection', 'Collection');
                      } else if (state.activeModule === 'singlePayment') {
                        onSubNavClick('singlePayment', '/single-payment', 'Payment');
                      } else if (state.activeModule === 'accountTransfer') {
                        onSubNavClick('accountTransfer', '/sub-account-transfer', 'Account Transfer');
                      }
                    }}
                  />
                )}
              </div>
            </Box>
            <div className="hiddenFormsPrint">
              <Box flx="row" gap="sm">
                {Object.values(scheduledTransactionsResponse.ScheduledTransactions)[0]?.Status === 'in progress' &&
                  permissions.TransactionCancellation && (
                    <Button
                      id="cancel-scheduled=transaction"
                      label={t('cancelTransactionButton')}
                      color="secondary"
                      onClick={() => {
                        setShowCancelTransactionConfirmAlert(true);
                      }}
                    />
                  )}
                <div className={styles.dropdownContainer}>
                  <Button
                    id="export-transactions"
                    color="none"
                    label={t('ExportDropdown.export')}
                    height="42px"
                    textSize={1}
                    background="transparent"
                    noShadow
                    icon="arrowDown"
                    iconLocation="right"
                    onClick={() => {
                      setIsMenuVisible(true);
                    }}
                  />
                  {isMenuVisible && (
                    <DropDown isMenuVisible={isMenuVisible} onClick={setIsMenuVisible}>
                      <Button
                        id="export-PDF-btn"
                        label={t('ExportDropdown.Options.pdf')}
                        background="transparent"
                        textSize={1}
                        width="100%"
                        noShadow
                        onClick={() => {
                          setIsExportingPDF(true);
                        }}
                      />
                      <Button
                        id="export-Print-btn"
                        label={t('ExportDropdown.Options.print')}
                        background="transparent"
                        textSize={1}
                        width="100%"
                        noShadow
                        onClick={() => {
                          setIsPrinting(true);
                        }}
                      />
                    </DropDown>
                  )}
                </div>
              </Box>
            </div>
          </Box>

          {scheduledTransactionsResponse.ScheduledTransactions[0] && (
            <>
              <ScheduledTransactionOverview transaction={scheduledTransactionsResponse.ScheduledTransactions[0]} />
              <TransactionsHistory
                transaction={scheduledTransactionsResponse.ScheduledTransactions[0]}
                setNewStatus={setNewStatus}
              />
            </>
          )}
        </Box>
      </div>
      <CancelTransactionDialogue handleDialogue={handlers.handleCancelTransactionDialogue} />
    </CancelTransactionContext.Provider>
  );
};

export default ScheduledTransactionDetailsContainer;
